// ProfilePage.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import '../styles/profile/Profile.css';
import { getCurrentUser, getCurrentAffiliation, getPerfiles } from '../services/authService';
import { getAccessToken, getRefreshToken, refreshAccessToken } from '../services/api';
import { AfiliacionesService } from '../services/afiliacionesServices';
import MarcasPendientesPopup from './MarcasPendientesPopup';
import { getS3ImageUrl } from '../services/s3Service';
import { getCachedImage } from '../utils/cmsCache';
import ProfileAvatar from './profile/ProfileAvatar';
import EditarDatosPerfil from './profile/EditarDatosPerfil';

/**
 * Calcula la edad a partir de la fecha de nacimiento
 * @param {string} fechaNacimiento - Fecha en formato YYYY-MM-DD o ISO string
 * @returns {number|null} - Edad en años o null si no se puede calcular
 */
const calcularEdad = (fechaNacimiento) => {
  if (!fechaNacimiento) return null;
  
  try {
    let fecha;
    if (typeof fechaNacimiento === 'string' && fechaNacimiento.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // Formato YYYY-MM-DD
      const [year, month, day] = fechaNacimiento.split('-').map(Number);
      fecha = new Date(year, month - 1, day);
    } else {
      fecha = new Date(fechaNacimiento);
    }
    
    if (isNaN(fecha.getTime())) return null;
    
    const hoy = new Date();
    let edad = hoy.getFullYear() - fecha.getFullYear();
    const mesDiff = hoy.getMonth() - fecha.getMonth();
    
    // Ajustar si aún no ha cumplido años este año
    if (mesDiff < 0 || (mesDiff === 0 && hoy.getDate() < fecha.getDate())) {
      edad--;
    }
    
    return edad;
  } catch (error) {
    console.error('Error al calcular edad:', error);
    return null;
  }
};

// Etiqueta legible del tipo de precio (subjunior/junior/open, con/sin club)
const labelTipoPrecio = (tipo) => {
  if (!tipo) return '';
  const labels = {
    subjunior_club: 'SubJunior (<20 años) con club',
    subjunior_sin_club: 'SubJunior (<20 años) sin club',
    junior_club: 'Junior (20 a <24 años) con club',
    junior_sin_club: 'Junior (20 a <24 años) sin club',
    open_club: 'Open (24+ años) con club',
    open_sin_club: 'Open (24+ años) sin club'
  };
  return labels[tipo] || (tipo.startsWith('open') ? 'Open' : tipo.startsWith('junior') ? 'Junior' : 'SubJunior');
};

const ProfilePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  // Cabecera editable desde el CMS ("Página Mi Perfil - Cabecera")
  const PROFILE_IMAGE_DEFAULT = `${process.env.PUBLIC_URL || ''}/IMG_3165B9EABF43-13.jpeg`;
  const [headerImage, setHeaderImage] = useState(() => {
    const c = getCachedImage('page_hero_profile');
    return c === undefined ? undefined : (c || PROFILE_IMAGE_DEFAULT);
  });
  useEffect(() => {
    let cancelado = false;
    getS3ImageUrl('page_hero_profile')
      .then((url) => { if (!cancelado) setHeaderImage(url || PROFILE_IMAGE_DEFAULT); })
      .catch(() => { if (!cancelado) setHeaderImage(PROFILE_IMAGE_DEFAULT); });
    return () => { cancelado = true; };
  }, []);
  const [afiliacion, setAfiliacion] = useState(null);
  const [precioAfiliacionApi, setPrecioAfiliacionApi] = useState(null);
  const [loadingPrecioAfiliacion, setLoadingPrecioAfiliacion] = useState(false);
  const [errorPrecioAfiliacion, setErrorPrecioAfiliacion] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [refrescandoAfiliacion, setRefrescandoAfiliacion] = useState(false);
  const [editandoDatos, setEditandoDatos] = useState(false);
  const [datosGuardados, setDatosGuardados] = useState(false);

  // Aplica cambios del propio usuario (datos editables o foto) sin recargar la página
  const aplicarCambiosUsuario = useCallback((cambios) => {
    setUser((prev) => {
      if (!prev) return prev;
      const nuevo = { ...prev, ...cambios };
      if ('nombre' in cambios) {
        nuevo.username = `${nuevo.nombre || ''} ${nuevo.apellido || ''}`.trim() || nuevo.email;
      }
      return nuevo;
    });
  }, []);

  const userRut = user?.rut ?? null;

  // Precio de afiliación desde el backend (según edad y club del usuario: subjunior/junior/open)
  const precioAfiliacion = useMemo(() => {
    if (!precioAfiliacionApi) return null;
    const t = precioAfiliacionApi.tipo || '';
    const categoria = t.startsWith('open') ? 'open' : t.startsWith('junior') ? 'junior' : 'subjunior';
    return {
      precio: precioAfiliacionApi.precio,
      categoria,
      moneda: precioAfiliacionApi.moneda || 'CLP',
      tipo: precioAfiliacionApi.tipo
    };
  }, [precioAfiliacionApi]);

  const refrescarAfiliacion = useCallback(async () => {
    setRefrescandoAfiliacion(true);
    try {
      const afiliacionData = await getCurrentAffiliation();
      setAfiliacion(afiliacionData);
    } catch (affError) {
      console.warn('ProfilePage: No se pudo refrescar afiliación:', affError);
    } finally {
      setRefrescandoAfiliacion(false);
    }
  }, []);

  // Obtener precio de afiliación desde el backend (open/junior, con/sin club)
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    setLoadingPrecioAfiliacion(true);
    AfiliacionesService.getPrecioAfiliacion()
      .then((res) => {
        if (!cancelled) {
          const data = res?.precio_afiliacion || res;
          if (data && (data.precio != null || data.precio === 0)) setPrecioAfiliacionApi(data);
        }
      })
      .catch((err) => {
        if (!cancelled) console.warn('ProfilePage: No se pudo obtener precio de afiliación:', err);
      })
      .finally(() => {
        if (!cancelled) setLoadingPrecioAfiliacion(false);
      });
    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => {
    const loadUserData = async () => {
      let token = getAccessToken();
      const loggedIn = localStorage.getItem('isLoggedIn');

      // Si no hay token, intentar renovar sesión con refresh_token antes de redirigir
      if (!token && getRefreshToken()) {
        try {
          await refreshAccessToken();
          token = getAccessToken();
        } catch (e) {
          console.warn('ProfilePage: No se pudo renovar la sesión:', e.message);
        }
      }

      if (!token) {
        if (loggedIn) {
          localStorage.removeItem('isLoggedIn');
          localStorage.removeItem('user');
          localStorage.removeItem('refresh_token');
        }
        setIsLoading(false);
        navigate('/login');
        return;
      }

      // Asegurar estado de logueado por si solo teníamos refresh_token
      if (!loggedIn) {
        localStorage.setItem('isLoggedIn', 'true');
      }

      try {
        const userData = await getCurrentUser();
        
        // Intentar obtener afiliación (puede fallar si el endpoint no está desplegado)
        let afiliacionData = null;
        try {
          afiliacionData = await getCurrentAffiliation();
        } catch (affError) {
          console.warn('ProfilePage: No se pudo obtener afiliación (puede que el endpoint no esté desplegado):', affError);
          // Continuar sin afiliación
        }
        
        console.log('ProfilePage: Datos recibidos del backend:', userData);
        console.log('ProfilePage: Datos de afiliación:', afiliacionData);
        
        if (!userData) {
          setError('No se pudieron obtener los datos del usuario');
          navigate('/login');
          return;
        }

        // Crear username completo si no existe (para compatibilidad)
        const userWithUsername = {
          ...userData,
          username: userData.username || `${userData.nombre} ${userData.apellido || ''}`.trim() || userData.email
        };
        console.log('ProfilePage: Usuario configurado:', userWithUsername);
        console.log('ProfilePage: Campos específicos - telefono:', userWithUsername.telefono, 'fecha_nacimiento:', userWithUsername.fecha_nacimiento, 'region:', userWithUsername.region, 'ciudad:', userWithUsername.ciudad, 'pais:', userWithUsername.pais);
        console.log('ProfilePage: Tipos de datos - telefono:', typeof userWithUsername.telefono, 'fecha_nacimiento:', typeof userWithUsername.fecha_nacimiento, 'region:', typeof userWithUsername.region, 'ciudad:', typeof userWithUsername.ciudad, 'pais:', typeof userWithUsername.pais);
        console.log('ProfilePage: Valores exactos - telefono:', JSON.stringify(userWithUsername.telefono), 'fecha_nacimiento:', JSON.stringify(userWithUsername.fecha_nacimiento), 'region:', JSON.stringify(userWithUsername.region), 'ciudad:', JSON.stringify(userWithUsername.ciudad), 'pais:', JSON.stringify(userWithUsername.pais));
        
        setUser(userWithUsername);
        setAfiliacion(afiliacionData);

        // Verificar si es admin
        const adminStatus = localStorage.getItem('isAdmin') === 'true';
        setIsAdmin(adminStatus);
      } catch (error) {
        console.error('Error al cargar datos del usuario desde el backend:', error);
        const msg = (error.message || '').toLowerCase();
        const isAuthError = msg.includes('token') || msg.includes('401') || msg.includes('expirad') || msg.includes('autenticación') || msg.includes('unauthorized');

        if (isAuthError) {
          setError(`Error al cargar datos del usuario: ${error.message}`);
          navigate('/login');
          return;
        }

        // Fallback: si /me falla (500, red, etc.) pero tenemos datos del login en localStorage, usarlos
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        if (storedUser && (storedUser.email || storedUser.nombre || storedUser.username)) {
          setUser({
            ...storedUser,
            username: storedUser.username || `${storedUser.nombre || ''} ${storedUser.apellido || ''}`.trim() || storedUser.email
          });
          setError('');
        } else {
          setError(`Error al cargar datos del usuario: ${error.message}`);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadUserData();
  }, [navigate]);

  // Si llegó desde pago-exitoso (?from=payment), esperar y refrescar afiliación (el webhook puede tardar unos segundos)
  const fromPayment = searchParams.get('from') === 'payment';
  useEffect(() => {
    if (!fromPayment || !userRut) return;
    const t = setTimeout(() => {
      refrescarAfiliacion();
      setSearchParams({}, { replace: true });
    }, 3000);
    return () => clearTimeout(t);
  }, [fromPayment, userRut, refrescarAfiliacion, setSearchParams]);

  // Obtener perfiles una sola vez por usuario (evitar bucle: no depender de user que cambia al hacer setUser)
  const perfilesFetchedRef = React.useRef(false);
  const lastUserRutRef = React.useRef(null);
  useEffect(() => {
    if (!userRut) return;
    if (lastUserRutRef.current !== userRut) {
      lastUserRutRef.current = userRut;
      perfilesFetchedRef.current = false;
    }
    if (perfilesFetchedRef.current) return;
    perfilesFetchedRef.current = true;
    getPerfiles()
      .then((perfiles) => {
        setUser((prev) => (prev ? { ...prev, perfiles: Array.isArray(perfiles) ? perfiles : [] } : prev));
      })
      .catch(() => {});
  }, [userRut]);

  useEffect(() => {
    if (isLoading || !user) return;
    if (location.hash === '#afiliacion') {
      setTimeout(() => {
        const el = document.getElementById('afiliacion');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 150);
    }
  }, [isLoading, user, location.hash]);

  if (isLoading) {
    return (
      <>
        <Header />
        <div style={{ 
          minHeight: '80vh', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}>
          <div>Cargando datos del perfil...</div>
        </div>
        <Footer />
      </>
    );
  }

  if (!user) {
    return (
      <>
        <Header />
        <div style={{ 
          minHeight: '80vh', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}>
          <div>
            {error ? (
              <div style={{ color: '#c62828', padding: '1rem' }}>
                ⚠️ {error}
              </div>
            ) : (
              <div>No se encontraron datos del usuario</div>
            )}
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <MarcasPendientesPopup
        onAceptadas={(marcas) => {
          if (!marcas) return;
          setUser((prev) => prev ? {
            ...prev,
            squat: marcas.squat,
            benchPress: marcas.bench_press,
            deadlift: marcas.deadlift,
            ranking: marcas.ranking,
            ranking_categoria: marcas.ranking_categoria,
          } : prev);
        }}
      />
      <Header />
      <div className="prof-page" style={{
        minHeight: 'calc(100vh - 200px)',
        padding: '2rem',
        backgroundColor: '#f1f1f1',
        marginTop: 0
      }}>
        <div className="prof-card" style={{
          maxWidth: '1000px',
          margin: '0 auto',
          backgroundColor: 'white',
          borderRadius: '20px',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
          overflow: 'hidden'
        }}>
          {/* Header del Perfil */}
          <div
            className="prof-header"
            style={{ backgroundImage: `linear-gradient(90deg, rgba(18,18,18,.96) 0%, rgba(18,18,18,.8) 60%, rgba(18,18,18,.55) 100%)${headerImage ? `, url(${headerImage})` : ''}` }}
          >
            <ProfileAvatar user={user} onChange={aplicarCambiosUsuario} />
            <div>
              <span className="ach-eyebrow-outline prof-eyebrow">Mi perfil</span>
              <h1 className="ach-display prof-name">
                {user.username}
              </h1>
              {user.club_nombre && <p className="prof-sub">{user.club_nombre}{afiliacion?.estado ? ` · Afiliación ${String(afiliacion.estado).toLowerCase()}` : ''}</p>}
            </div>
            {user.username === 'admin' && (
              <span style={{
                display: 'inline-block',
                padding: '0.5rem 1rem',
                background: '#ff4500',
                color: 'white',
                borderRadius: '20px',
                fontSize: '0.85rem',
                fontWeight: '600',
                textTransform: 'uppercase',
                marginTop: '0.5rem'
              }}>
                Administrador
              </span>
            )}
          </div>

          {/* Contenido del Perfil */}
          <div style={{ padding: '2rem' }}>
            {/* Navegación rápida dentro del perfil */}
            <nav className="prof-nav" aria-label="Secciones del perfil" style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.75rem',
              marginBottom: '1.5rem',
              paddingBottom: '1rem',
              borderBottom: '1px solid #eaeaea'
            }}>
              <a href="#informacion-personal" style={{ color: '#111', fontWeight: '600', textDecoration: 'none' }}>Datos personales</a>
              <span style={{ color: '#ccc' }}>|</span>
              <a href="#afiliacion" style={{ color: '#111', fontWeight: '600', textDecoration: 'none' }}>Afiliación</a>
              <span style={{ color: '#ccc' }}>|</span>
              <a href="#marcas-personales" style={{ color: '#111', fontWeight: '600', textDecoration: 'none' }}>Marcas personales</a>
              <span style={{ color: '#ccc' }}>|</span>
              <Link to="/profile/mis-eventos" style={{ color: '#111', fontWeight: '600', textDecoration: 'none' }}>Mis eventos</Link>
              {(() => {
                const perfiles = user.perfiles;
                const esPresidente = Array.isArray(perfiles) && perfiles.includes('presidente_club');
                const puedePostular = Array.isArray(perfiles) && (
                  perfiles.includes('presidente_club') || perfiles.includes('meet_director')
                );

                return (
                  <>
                    {puedePostular ? (
                      <>
                        <span style={{ color: '#ccc' }}>|</span>
                        <Link
                          to="/profile/postular-evento"
                          style={{ color: '#111', fontWeight: '600', textDecoration: 'none' }}
                        >
                          Postular un Evento (Competencia)
                        </Link>
                      </>
                    ) : null}
                    {esPresidente ? (
                      <>
                        <span style={{ color: '#ccc' }}>|</span>
                        <Link
                          to="/profile/presidente-club"
                          style={{ color: '#111', fontWeight: '600', textDecoration: 'none' }}
                        >
                          Administrar club
                        </Link>
                      </>
                    ) : null}
                  </>
                );
              })()}
            </nav>

            {/* Información Personal */}
            <section id="informacion-personal" style={{ marginBottom: '3rem' }}>
              <h2 style={{
                fontSize: '1.25rem',
                fontWeight: '500',
                color: '#1a1a1a',
                marginBottom: '1.5rem',
                letterSpacing: '-0.02em'
              }}>
                Información Personal
              </h2>
              <div className="prof-edit-bar">
                {datosGuardados && !editandoDatos && (
                  <span className="prof-edit-ok" role="status">✓ Datos actualizados</span>
                )}
                {!editandoDatos && (
                  <button
                    type="button"
                    className="ach-pill ach-pill--sm prof-edit-open"
                    onClick={() => { setEditandoDatos(true); setDatosGuardados(false); }}
                  >
                    Editar datos
                  </button>
                )}
              </div>
              {editandoDatos ? (
                <EditarDatosPerfil
                  user={user}
                  onCancelar={() => setEditandoDatos(false)}
                  onGuardado={(guardado) => {
                    aplicarCambiosUsuario(guardado || {});
                    setEditandoDatos(false);
                    setDatosGuardados(true);
                  }}
                />
              ) : (
              <div style={{
                maxWidth: '520px',
                margin: '0 auto',
                border: '1px solid #ebebeb',
                borderRadius: '6px',
                overflow: 'hidden',
                background: '#fff'
              }}>
                {[
                  { label: 'Nombre', value: user.nombre ? `${user.nombre}${user.apellido ? ` ${user.apellido}` : ''}` : user.username },
                  { label: 'RUT', value: user.rut },
                  { label: 'Club', value: user.club_nombre || '—' },
                  { label: 'Afiliación al club', value: user.club_activo ? 'Activa' : 'Inactiva', extra: user.club_activo ? <span style={{ fontSize: '0.7rem', color: '#22c55e', marginLeft: '6px', fontWeight: '500' }}>✓</span> : null },
                  { label: 'Email', value: user.email, extra: user.email_verificado && <span style={{ fontSize: '0.7rem', color: '#22c55e', marginLeft: '6px', fontWeight: '500' }}>✓ Verificado</span> },
                  { label: 'Teléfono', value: user.telefono },
                  ...(user.telefono ? [{ label: 'Teléfono verificado', value: user.telefono_verificado ? 'Sí' : 'No' }] : []),
                  { label: 'Fecha de nacimiento', value: user.fecha_nacimiento ? (() => {
                    const fechaStr = user.fecha_nacimiento;
                    let fecha;
                    if (typeof fechaStr === 'string' && fechaStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
                      const [y, m, d] = fechaStr.split('-').map(Number);
                      fecha = new Date(y, m - 1, d);
                    } else {
                      fecha = new Date(fechaStr);
                    }
                    return fecha.toLocaleDateString('es-CL', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'America/Santiago' });
                  })() : null },
                  { label: 'Región', value: user.region },
                  { label: 'Ciudad', value: user.ciudad },
                  { label: 'País', value: user.pais },
                  { label: 'Dirección', value: user.direccion },
                  { label: 'Estado', value: user.estado }
                ].filter(({ value }) => value || value === 0).map((item, i, arr) => (
                  <div
                    key={item.label}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      padding: '0.75rem 1rem',
                      borderBottom: i < arr.length - 1 ? '1px solid #eee' : 'none',
                      gap: '1rem',
                      flexWrap: 'wrap'
                    }}
                  >
                    <span style={{
                      fontSize: '0.8rem',
                      color: '#888',
                      fontWeight: '500',
                      minWidth: '140px'
                    }}>
                      {item.label}
                    </span>
                    <span style={{
                      fontSize: '0.95rem',
                      color: '#1a1a1a',
                      textAlign: 'right',
                      flex: 1
                    }}>
                      {item.value}
                      {item.extra}
                    </span>
                  </div>
                ))}
              </div>
              )}
            </section>

            {/* Información de Afiliación */}
            <section id="afiliacion" style={{ marginBottom: '3rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.5rem' }}>
                <h2 style={{
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  color: '#111',
                  margin: 0,
                  paddingBottom: '0.75rem',
                  borderBottom: '2px solid #eaeaea',
                  flex: 1
                }}>
                  Información de Afiliación
                </h2>
                <button
                  type="button"
                  onClick={refrescarAfiliacion}
                  disabled={refrescandoAfiliacion}
                  style={{
                    padding: '8px 16px',
                    background: '#f0f7ff',
                    color: '#111',
                    border: '1.5px solid #111',
                    borderRadius: '6px',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    cursor: refrescandoAfiliacion ? 'wait' : 'pointer'
                  }}
                >
                  {refrescandoAfiliacion ? 'Actualizando...' : 'Refrescar afiliación'}
                </button>
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '1.5rem'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    color: '#666',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '0.5rem'
                  }}>
                    Género para Competir
                  </label>
                  <div style={{
                    padding: '0.75rem',
                    background: '#f8f9fa',
                    borderRadius: '6px',
                    border: '1px solid #eaeaea',
                    fontSize: '1rem',
                    color: '#333'
                  }}>
                    {user.genero === 'M' ? 'Hombre' : user.genero === 'F' ? 'Mujer' : user.genero || 'No especificado'}
                  </div>
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    color: '#666',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '0.5rem'
                  }}>
                    Categoría
                  </label>
                  <div style={{
                    padding: '0.75rem',
                    background: '#f8f9fa',
                    borderRadius: '6px',
                    border: '1px solid #eaeaea',
                    fontSize: '1rem',
                    color: '#333'
                  }}>
                    {precioAfiliacion?.tipo ? labelTipoPrecio(precioAfiliacion.tipo) : '—'}
                  </div>
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    color: '#666',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '0.5rem'
                  }}>
                    Número de Afiliación
                  </label>
                  <div style={{
                    padding: '0.75rem',
                    background: '#f8f9fa',
                    borderRadius: '6px',
                    border: '1px solid #eaeaea',
                    fontSize: '1rem',
                    color: '#333'
                  }}>
                    {afiliacion?.numero_afiliacion || 'No asignado'}
                  </div>
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    color: '#666',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '0.5rem'
                  }}>
                    Fecha de Inicio
                  </label>
                  <div style={{
                    padding: '0.75rem',
                    background: '#f8f9fa',
                    borderRadius: '6px',
                    border: '1px solid #eaeaea',
                    fontSize: '1rem',
                    color: '#333'
                  }}>
                    {afiliacion?.fecha_inicio 
                      ? (() => {
                          const fechaStr = afiliacion.fecha_inicio;
                          let fecha;
                          
                          if (typeof fechaStr === 'string' && fechaStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
                            const [year, month, day] = fechaStr.split('-').map(Number);
                            fecha = new Date(year, month - 1, day);
                          } else {
                            fecha = new Date(fechaStr);
                          }
                          
                          return fecha.toLocaleDateString('es-CL', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric',
                            timeZone: 'America/Santiago'
                          });
                        })()
                      : 'No asignada'}
                  </div>
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    color: '#666',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '0.5rem'
                  }}>
                    Fecha de Vencimiento
                  </label>
                  <div style={{
                    padding: '0.75rem',
                    background: '#f8f9fa',
                    borderRadius: '6px',
                    border: '1px solid #eaeaea',
                    fontSize: '1rem',
                    color: '#333'
                  }}>
                    {afiliacion?.fecha_fin 
                      ? (() => {
                          const fechaStr = afiliacion.fecha_fin;
                          let fecha;
                          
                          if (typeof fechaStr === 'string' && fechaStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
                            const [year, month, day] = fechaStr.split('-').map(Number);
                            fecha = new Date(year, month - 1, day);
                          } else {
                            fecha = new Date(fechaStr);
                          }
                          
                          return fecha.toLocaleDateString('es-CL', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric',
                            timeZone: 'America/Santiago'
                          });
                        })()
                      : 'No asignada'}
                  </div>
                </div>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    color: '#666',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '0.5rem'
                  }}>
                    Estado de Afiliación
                  </label>
                  {afiliacion?.estado ? (
                    <div style={{
                      padding: '0.5rem 1rem',
                      borderRadius: '20px',
                      fontSize: '0.85rem',
                      fontWeight: '600',
                      display: 'inline-block',
                      background: afiliacion.estado.toLowerCase() === 'activa' 
                        ? '#e8f5e9' 
                        : afiliacion.estado.toLowerCase() === 'vencida'
                        ? '#fff3e0'
                        : '#ffebee',
                      color: afiliacion.estado.toLowerCase() === 'activa' 
                        ? '#2e7d32' 
                        : afiliacion.estado.toLowerCase() === 'vencida'
                        ? '#f57c00'
                        : '#c62828',
                      border: `1px solid ${afiliacion.estado.toLowerCase() === 'activa' ? '#4caf50' : afiliacion.estado.toLowerCase() === 'vencida' ? '#ff9800' : '#ef5350'}`
                    }}>
                      {afiliacion.estado.charAt(0).toUpperCase() + afiliacion.estado.slice(1)}
                    </div>
                  ) : (
                    <div style={{
                      padding: '0.75rem',
                      background: '#f8f9fa',
                      borderRadius: '6px',
                      border: '1px solid #eaeaea',
                      fontSize: '1rem',
                      color: '#333'
                    }}>
                      No asignado
                    </div>
                  )}
                </div>
              </div>
              
              {/* Botón de Pago de Afiliación - oculto si afiliación ya está pagada/activa */}
              {!(afiliacion?.estado && String(afiliacion.estado).toLowerCase() === 'activa') && (
              <div style={{
                marginTop: '2rem',
                padding: '1.5rem',
                background: 'linear-gradient(90deg, #D52B1E 0%, #0039A6 100%)',
                borderRadius: '16px',
                textAlign: 'center'
              }}>
                {/* Información del precio desde el backend */}
                {loadingPrecioAfiliacion && (
                  <div style={{ marginBottom: '1rem', color: 'white', fontSize: '0.9rem' }}>Cargando precio...</div>
                )}
                {!loadingPrecioAfiliacion && precioAfiliacion && (
                  <div style={{
                    marginBottom: '1rem',
                    padding: '0.75rem',
                    background: 'rgba(255, 255, 255, 0.15)',
                    borderRadius: '6px',
                    color: 'white'
                  }}>
                    <div style={{ fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                      Precio de Afiliación: <strong>${precioAfiliacion.precio.toLocaleString('es-CL')} {precioAfiliacion.moneda}</strong>
                    </div>
                    <div style={{ fontSize: '0.8rem', opacity: 0.9 }}>
                      {precioAfiliacion.tipo ? labelTipoPrecio(precioAfiliacion.tipo) : (precioAfiliacion.categoria === 'open' ? 'Open' : precioAfiliacion.categoria === 'junior' ? 'Junior' : 'SubJunior')}
                    </div>
                  </div>
                )}
                
                <button
                  type="button"
                  disabled={loadingPrecioAfiliacion || !precioAfiliacion}
                  onClick={() => {
                    try {
                      // Precio desde el backend (GET /api/afiliacion/precio)
                      const price = precioAfiliacion?.precio || 
                                   (afiliacion?.monto && Number(afiliacion.monto) > 0
                                     ? Number(afiliacion.monto)
                                     : 40000);
                      
                      // Mismo formato y key que CartPage/ShopPage: 'cart' con { producto_id, cantidad, producto: { nombre, precio, imagen_url } }
                      const rawCart = (() => {
                        try {
                          const raw = localStorage.getItem('cart');
                          const arr = raw ? JSON.parse(raw) : [];
                          return Array.isArray(arr) ? arr : [];
                        } catch {
                          return [];
                        }
                      })();
                      const productoId = 'afiliacion-achipo';
                      const categoriaNombre = precioAfiliacion?.categoria === 'open' ? 'Open' : precioAfiliacion?.categoria === 'junior' ? 'Junior' : 'SubJunior';
                      const existing = rawCart.find(item => item.producto_id === productoId);
                      let updated;
                      if (existing) {
                        // Actualizar tanto la cantidad como el precio para asegurar consistencia
                        updated = rawCart.map(item =>
                          item.producto_id === productoId 
                            ? { 
                                ...item, 
                                cantidad: (item.cantidad || 1) + 1,
                                producto: {
                                  ...item.producto,
                                  nombre: `Afiliación ACHIPO (${categoriaNombre})`,
                                  precio: price,  // Actualizar precio también
                                  imagen_url: `${process.env.PUBLIC_URL || ''}/achipo.jpg`  // Asegurar que la imagen esté presente
                                }
                              } 
                            : item
                        );
                      } else {
                        updated = [
                          ...rawCart,
                          {
                            producto_id: productoId,
                            cantidad: 1,
                            producto: {
                              nombre: `Afiliación ACHIPO (${categoriaNombre})`,
                              precio: price,
                              imagen_url: `${process.env.PUBLIC_URL || ''}/achipo.jpg`
                            }
                          }
                        ];
                      }
                      localStorage.setItem('cart', JSON.stringify(updated));
                      window.dispatchEvent(new Event('cart-updated'));
                      navigate('/cart');
                    } catch (err) {
                      console.error('Error al agregar afiliación al carrito:', err);
                      alert('No se pudo agregar al carrito. Intenta de nuevo.');
                    }
                  }}
                  style={{
                    padding: '1rem 2rem',
                    background: 'white',
                    color: '#111',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '1rem',
                    fontWeight: '700',
                    cursor: (loadingPrecioAfiliacion || !precioAfiliacion) ? 'not-allowed' : 'pointer',
                    opacity: (loadingPrecioAfiliacion || !precioAfiliacion) ? 0.7 : 1,
                    transition: 'all 0.3s ease',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
                  }}
                >
                  💳 Paga tu Afiliación
                </button>
                <p style={{
                  marginTop: '1rem',
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '0.9rem'
                }}>
                  Agrega tu afiliación al carrito y paga de forma segura con Flow
                </p>
              </div>
              )}
            </section>

            {/* Marcas Personales */}
            <section id="marcas-personales">
              <h2 style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                color: '#111',
                marginBottom: '1.5rem',
                paddingBottom: '0.75rem',
                borderBottom: '2px solid #eaeaea'
              }}>
                Marcas Personales
              </h2>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1.5rem',
                marginBottom: '1.5rem'
              }}>
                <div style={{
                  padding: '1.5rem',
                  background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                  borderRadius: '8px',
                  border: '1px solid #eaeaea',
                  textAlign: 'center'
                }}>
                  <div style={{
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    color: '#666',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '0.75rem'
                  }}>
                    Squat
                  </div>
                  <div style={{
                    fontSize: '2rem',
                    fontWeight: '700',
                    color: '#111'
                  }}>
                    {user.squat ? `${user.squat} kg` : '—'}
                  </div>
                  {!user.squat && (
                    <div style={{
                      fontSize: '0.75rem',
                      color: '#999',
                      marginTop: '0.25rem'
                    }}>
                      No registrada
                    </div>
                  )}
                </div>
                <div style={{
                  padding: '1.5rem',
                  background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                  borderRadius: '8px',
                  border: '1px solid #eaeaea',
                  textAlign: 'center'
                }}>
                  <div style={{
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    color: '#666',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '0.75rem'
                  }}>
                    Bench Press
                  </div>
                  <div style={{
                    fontSize: '2rem',
                    fontWeight: '700',
                    color: '#111'
                  }}>
                    {user.benchPress ? `${user.benchPress} kg` : '—'}
                  </div>
                  {!user.benchPress && (
                    <div style={{
                      fontSize: '0.75rem',
                      color: '#999',
                      marginTop: '0.25rem'
                    }}>
                      No registrada
                    </div>
                  )}
                </div>
                <div style={{
                  padding: '1.5rem',
                  background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                  borderRadius: '8px',
                  border: '1px solid #eaeaea',
                  textAlign: 'center'
                }}>
                  <div style={{
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    color: '#666',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    marginBottom: '0.75rem'
                  }}>
                    Deadlift
                  </div>
                  <div style={{
                    fontSize: '2rem',
                    fontWeight: '700',
                    color: '#111'
                  }}>
                    {user.deadlift ? `${user.deadlift} kg` : '—'}
                  </div>
                  {!user.deadlift && (
                    <div style={{
                      fontSize: '0.75rem',
                      color: '#999',
                      marginTop: '0.25rem'
                    }}>
                      No registrada
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* Sección de Administración - Solo visible para admins */}
            {isAdmin && (
              <section style={{ marginTop: '2rem' }}>
                <h2 style={{
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  color: '#9c27b0',
                  marginBottom: '1.5rem',
                  paddingBottom: '0.75rem',
                  borderBottom: '2px solid #9c27b0'
                }}>
                  Administracion
                </h2>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '1.5rem'
                }}>
                  <Link to="/admin/cms" style={{ textDecoration: 'none' }}>
                    <div style={{
                      padding: '1.5rem',
                      background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
                      borderRadius: '12px',
                      color: 'white',
                      cursor: 'pointer',
                      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                      boxShadow: '0 4px 15px rgba(156, 39, 176, 0.3)'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(156, 39, 176, 0.4)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(156, 39, 176, 0.3)';
                    }}
                    >
                      <div style={{
                        fontSize: '2rem',
                        marginBottom: '0.75rem'
                      }}>
                        Gestor de Contenido
                      </div>
                      <div style={{
                        fontSize: '1.1rem',
                        fontWeight: '600',
                        marginBottom: '0.5rem'
                      }}>
                        CMS del Landing
                      </div>
                      <div style={{
                        fontSize: '0.9rem',
                        opacity: 0.9
                      }}>
                        Edita imagenes y textos del sitio web
                      </div>
                    </div>
                  </Link>
                </div>
              </section>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ProfilePage;

